import { useState } from "react";
import type { InvitedEntryDecision } from "@verity-connect/contracts";
import { PASSWORD_MINIMUM_LENGTH } from "@verity-connect/domain";
import { TurnstileField, turnstileToken } from "./TurnstileField";
import {
  cancelled,
  deviceClientId,
  describe,
  enrolPasskey,
  passkeysAvailable,
  signInWithPasskey,
  type PasskeyDoor,
} from "./usePasskeys";

/**
 * One field and one button, on every door.
 *
 * All three sign-in screens used to carry two buttons of equal weight — one for a
 * passkey, one for an emailed code — with a line of text between them apologising for
 * the fork. Nothing told anybody which was theirs, and the screen was seven hundred
 * pixels of explanation before the thing you came to do.
 *
 * The fork is gone rather than rearranged. The passkey now lives inside the email
 * field's own autofill list, where the browser keeps saved sign-ins and where somebody
 * is already looking. Everyone else types an address, which is what they expected to
 * do. What is left underneath is a quiet way back for a browser that cannot offer the
 * list, and it is deliberately not a button that competes with anything.
 *
 * The address is asked about before anything is sent, when the door can answer. Every
 * invited door used to do one thing and one thing only: post a code. Somebody who held a
 * password was sent a code anyway; somebody who held nothing was sent a code they then
 * had to fetch before they could choose one; somebody whose passkey lived on another
 * device was offered that and nothing else. All three were told to go and get an email
 * for an account the association had already vouched for. Now the door says what it has
 * and offers all of it, with the code still there for whoever wants it.
 */
export function SignInCard({
  door,
  title,
  fieldLabel,
  fieldHint,
  placeholder,
  submitLabel,
  pendingLabel,
  pending,
  error,
  intro,
  onSubmit,
  onResolve,
  emailedCode = true,
  onPassword,
  footer,
}: {
  door: PasskeyDoor;
  title: string;
  fieldLabel: string;
  fieldHint: string;
  placeholder?: string;
  submitLabel: string;
  pendingLabel: string;
  pending: boolean;
  error?: string | null;
  onSubmit: (email: string, humanCheck: string) => void;
  /** Asks the door what this address holds. Absent on a door that cannot answer. */
  /** Shown above the email field only. A door's other way in belongs to that step alone. */
  intro?: React.ReactNode;
  onResolve?: (email: string, humanCheck: string) => Promise<InvitedEntryDecision>;
  /** Whether this door offers to email a code when somebody would rather not use a password. */
  emailedCode?: boolean;
  /** Signs in with a password, or writes the first one. */
  onPassword?: (email: string, password: string) => Promise<void>;
  footer?: React.ReactNode;
}): React.JSX.Element {
  const [email, setEmail] = useState("");
  const [decision, setDecision] = useState<InvitedEntryDecision | null>(null);
  const [password, setPassword] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [working, setWorking] = useState(false);
  const [offerDevice, setOfferDevice] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [passkeyError, setPasskeyError] = useState<string | null>(null);
  // Hoisted: both the confirmation screen and the password form read it, and the
  // confirmation screen renders first.
  const choosing = decision?.kind === "invited";
  const fieldId = `${door}-sign-in-email`;
  const hintId = `${fieldId}-hint`;

  const usePasskeyDirectly = async () => {
    setPasskeyBusy(true);
    setPasskeyError(null);
    try {
      await signInWithPasskey(deviceClientId(), door);
      window.location.reload();
    } catch (reason) {
      setPasskeyError(cancelled(reason) ? null : describe(reason));
    } finally {
      setPasskeyBusy(false);
    }
  };

  if (offerDevice) {
    return (
      <section className="sign-in-card">
        <h1 data-page-heading tabIndex={-1}>You're signed in</h1>
        <p className="sign-in-hint">
          {choosing ? "Your password is set." : "You're in."} Add this device as well and next
          time is a look or a touch — the password still works everywhere else.
        </p>
        {passkeyError && <p className="form-error" role="alert">{passkeyError}</p>}
        <button
          className="primary-action"
          type="button"
          disabled={passkeyBusy}
          onClick={() => {
            setPasskeyBusy(true);
            setPasskeyError(null);
            void enrolPasskey(door)
              .then(() => window.location.assign("/"))
              .catch((reason: unknown) => {
                setPasskeyError(cancelled(reason) ? null : describe(reason));
                setPasskeyBusy(false);
              });
          }}
        >
          {passkeyBusy ? "Adding this device…" : "Add Face ID, Touch ID, or your device"}
        </button>
        <button className="sign-in-alternative" type="button" onClick={() => window.location.assign("/")}>
          Not now
        </button>
      </section>
    );
  }

  if (decision && onPassword) {
    return (
      <section className="sign-in-card">
        <h1 data-page-heading tabIndex={-1}>{choosing ? "Choose your password" : "Welcome back"}</h1>
        <p className="sign-in-hint">
          {choosing
            ? "This address has been invited, so there is no code to wait for. Choose the password you will use from now on."
            : "Enter your password."}
        </p>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (working || password.length < (choosing ? PASSWORD_MINIMUM_LENGTH : 1)) return;
            setPasskeyError(null);
            setWorking(true);
            void onPassword(email, password)
              // Signed in either way. A password that works and a device that remembers you
              // are both worth having, so the second is offered rather than skipped past.
              // Offered to anybody who has no passkey on this host, not only to somebody
              // setting a first password. A passkey is bound to the domain it was made on,
              // so a person can hold one at the guide and still have nothing to answer with
              // here — which is exactly how an administrator went eighteen days signing in
              // by password on a screen that claimed he had a device.
              .then(() => { if (!decision.hasPasskey && passkeysAvailable()) setOfferDevice(true); else window.location.assign("/"); })
              .catch((reason: unknown) => {
                setPasskeyError(reason instanceof Error ? reason.message : "That did not sign you in.");
              })
              .finally(() => setWorking(false));
          }}
        >
          <p className="sign-in-hint"><strong>{email}</strong></p>
          <label htmlFor={`${fieldId}-password`}>{choosing ? "New password" : "Password"}</label>
          <input
            id={`${fieldId}-password`}
            name="password"
            type={revealed ? "text" : "password"}
            autoComplete={choosing ? "new-password" : "current-password"}
            required
            autoFocus
            minLength={choosing ? PASSWORD_MINIMUM_LENGTH : undefined}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <button className="sign-in-alternative" type="button" aria-pressed={revealed} onClick={() => setRevealed((value) => !value)}>
            {revealed ? "Hide password" : "Show password"}
          </button>
          {choosing && <p className="sign-in-hint">At least {PASSWORD_MINIMUM_LENGTH} characters.</p>}
          {(error ?? passkeyError) && <p className="form-error" role="alert">{error ?? passkeyError}</p>}
          <button className="primary-action" type="submit" disabled={working}>
            {working ? "Signing in…" : choosing ? "Create sign-in" : "Sign in"}
          </button>
        </form>

        {decision.hasPasskey && passkeysAvailable() && (
          <button className="sign-in-alternative" type="button" disabled={passkeyBusy} onClick={() => void usePasskeyDirectly()}>
            {passkeyBusy ? "Checking your device…" : "Or use Face ID, Touch ID, or your device"}
          </button>
        )}
        {/* A door whose people were vouched for by the association does not offer this.
            Being in the association's own records is the verification; a code emailed
            back to the address they were invited at proves nothing further, and staff can
            already hand a presenter a sign-in link when a password is forgotten. */}
        {emailedCode && (
          <button className="sign-in-alternative" type="button" disabled={pending} onClick={() => onSubmit(email, "")}>
            {pending ? pendingLabel : "Email me a code instead"}
          </button>
        )}
        <button className="sign-in-alternative" type="button" onClick={() => { setDecision(null); setPassword(""); setPasskeyError(null); }}>
          Use a different address
        </button>
        {footer}
      </section>
    );
  }

  return (
    <section className="sign-in-card">
      {intro}
      <h1 data-page-heading tabIndex={-1}>{title}</h1>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          const form = event.currentTarget;
          const data = new FormData(form);
          // Read from the field rather than from state. A browser filling the address in
          // for somebody does not always tell React it did, which used to leave the form
          // holding an empty string while the screen plainly showed an address.
          const typed = String(data.get("email") ?? "").trim() || email;
          if (!typed.includes("@")) {
            setPasskeyError("Enter the email address to sign in with.");
            return;
          }
          setPasskeyError(null);
          // Read through the helper, which also asks for a fresh answer. Cloudflare
          // redeems each one once, so a corrected address pressed a second time used to
          // resend a spent answer and be refused for not being a person.
          const humanCheck = turnstileToken(form);
          if (!onResolve) { onSubmit(typed, humanCheck); return; }
          setWorking(true);
          void onResolve(typed, humanCheck)
            .then((next) => { setEmail(next.email); setDecision(next); })
            // A door that cannot answer says so and stops. It deliberately does not fall
            // back to emailing a code: an unasked-for code is the thing this screen exists
            // to stop sending, and a lookup that failed is no reason to send one.
            .catch((reason: unknown) => {
              setPasskeyError(reason instanceof Error ? reason.message : "That address could not be checked.");
            })
            .finally(() => setWorking(false));
        }}
      >
        <label htmlFor={fieldId}>{fieldLabel}</label>
        <p className="sign-in-hint" id={hintId}>{fieldHint}</p>
        <input
          id={fieldId}
          name="email"
          type="email"
          // Deliberately not the `webauthn` token. It asks the browser to offer saved
          // passkeys inside this field, which draws a small panel over the form the
          // moment somebody starts typing their address — in the way, every time, for
          // the one person in a hundred it helps. The button below offers the same
          // thing when it is actually wanted.
          autoComplete="username"
          required
          autoFocus
          placeholder={placeholder}
          aria-describedby={hintId}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <TurnstileField />
        {(error ?? passkeyError) && (
          <p className="form-error" role="alert">{error ?? passkeyError}</p>
        )}
        {/* Only ever disabled while a request is in flight. A disabled submit button
            stops the Enter key submitting the form at all, which is why this form could
            only be sent by clicking it — and a button greyed out with no explanation is
            a worse way to say "that address looks wrong" than saying so. */}
        <button className="primary-action" type="submit" disabled={pending || working}>
          {pending || working ? pendingLabel : submitLabel}
        </button>
      </form>

      {/* Offered blind only where the door can never know. This asked nothing about the
          person in front of it — only whether the browser could do passkeys at all — so a
          presenter signing in for the first time was offered a saved key she had never
          made, directly under a red error. A door that can look an address up offers it
          one step later instead, where the answer is known. */}
      {!onResolve && passkeysAvailable() && (
        <button className="sign-in-alternative" type="button" disabled={passkeyBusy} onClick={() => void usePasskeyDirectly()}>
          {passkeyBusy ? "Checking your device…" : "Use this device instead"}
        </button>
      )}

      {footer}
    </section>
  );
}
